// The app's voice audio: one AudioContext for everything.
// - Speech the assistant says (raw PCM streamed from /api/voice/speak) is played
//   through an analyser, so the face can move its mouth with the real sound.
// - The microphone is recorded through the same context (recorder.js).
// Browsers start sound only after a user gesture: call unlockAudio() inside a
// pointerdown / keydown handler before the first sound.

let ctx = null;
let out = null;
let analyser = null;
let timeData = null;
let freqData = null;

export function audio() {
  if (ctx) return { ctx, out, analyser };
  const AC =
    typeof window !== 'undefined' &&
    (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  try {
    ctx = new AC({ latencyHint: 'interactive' });
  } catch (e) {
    ctx = new AC();
  }
  out = ctx.createGain();
  out.gain.value = 1;
  analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.35;
  out.connect(analyser);
  analyser.connect(ctx.destination);
  timeData = new Float32Array(analyser.fftSize);
  freqData = new Uint8Array(analyser.frequencyBinCount);
  return { ctx, out, analyser };
}

export function unlockAudio() {
  const a = audio();
  if (!a) return;
  if (a.ctx.state === 'suspended') a.ctx.resume().catch(() => {});
  try {
    // iOS needs a real (silent) sound started inside the gesture
    const b = a.ctx.createBuffer(1, 1, 22050);
    const s = a.ctx.createBufferSource();
    s.buffer = b;
    s.connect(a.ctx.destination);
    s.start(0);
  } catch (e) {
    // ignore
  }
}

/* What is being said right now: loudness 0..1 and the spectral centroid (Hz)
   — low centroid ≈ round vowels (o, u), high ≈ wide ones (e, i) and hiss. */
export function speechLevel() {
  if (!analyser || !ctx) return { level: 0, centroid: 0 };
  analyser.getFloatTimeDomainData(timeData);
  let sum = 0;
  for (let i = 0; i < timeData.length; i++) sum += timeData[i] * timeData[i];
  const rms = Math.sqrt(sum / timeData.length);
  analyser.getByteFrequencyData(freqData);
  let w = 0;
  let tot = 0;
  const top = Math.floor(freqData.length / 2); // up to ~12 kHz
  for (let i = 2; i < top; i++) {
    w += i * freqData[i];
    tot += freqData[i];
  }
  const centroid = tot ? ((w / tot) * ctx.sampleRate) / analyser.fftSize : 0;
  return { level: Math.min(1, rms * 5), centroid };
}

const pcmToFloat = (bytes) => {
  const n = bytes.length >> 1;
  const f = new Float32Array(n);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, n * 2);
  for (let i = 0; i < n; i++) f[i] = dv.getInt16(i * 2, true) / 32768;
  return f;
};

/* Plays segments of speech one after another, without gaps:
   enqueue({ text, emotion }) starts fetching the sound at once (two ahead),
   playback keeps their order. Segments whose sound fails are read by the
   browser's own voice; with `muted` nothing is fetched and every segment is
   "spoken" silently for about as long as reading it takes (the face still
   talks, the captions still move).
   Callbacks: onSegment(seg) when a segment starts being heard, onIdle() when
   everything queued has been said. mouth() → { level, centroid } now. */
export class Speaker {
  constructor({ voice = '', muted = false, onSegment, onIdle } = {}) {
    this.voice = voice;
    this.muted = muted;
    this.onSegment = onSegment || (() => {});
    this.onIdle = onIdle || (() => {});
    this.queue = [];
    this.sources = new Set();
    this.timers = new Set();
    this.cursor = 0; // audio-clock time when the queued sound ends
    this.pumping = false;
    this.fake = null; // { until } — silent / browser-voice speech
    this.gen = 0;
  }

  get busy() {
    return this.queue.length > 0 || this.pumping || this.isSounding();
  }

  isSounding() {
    const a = audio();
    if (this.fake && performance.now() < this.fake.until) return true;
    return Boolean(a && a.ctx.currentTime < this.cursor - 0.02);
  }

  mouth() {
    if (this.fake && performance.now() < this.fake.until) {
      // speech without our audio: a believable talking rhythm
      const t = performance.now() / 1000;
      const syl = Math.max(
        0,
        Math.sin(t * 13.5) * 0.6 + Math.sin(t * 7.3) * 0.4
      );
      return {
        level: 0.25 + syl * 0.55,
        centroid: 1400 + Math.sin(t * 3.1) * 700,
      };
    }
    return speechLevel();
  }

  enqueue(seg) {
    const item = { ...seg, ctrl: new AbortController(), res: null };
    this.queue.push(item);
    this.prefetch();
    this.pump();
  }

  prefetch() {
    if (this.muted) return;
    this.queue.slice(0, 2).forEach((it) => {
      if (it.res) return;
      it.res = fetch('/api/voice/speak', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: it.text,
          emotion: it.emotion,
          voice: this.voice || undefined,
        }),
        signal: it.ctrl.signal,
      }).catch((e) => e);
    });
  }

  later(ms, fn) {
    const id = setTimeout(
      () => {
        this.timers.delete(id);
        fn();
      },
      Math.max(0, ms)
    );
    this.timers.add(id);
  }

  async pump() {
    if (this.pumping) return;
    this.pumping = true;
    const gen = this.gen;
    try {
      while (this.queue.length && gen === this.gen) {
        const it = this.queue[0];
        await this.play(it, gen);
        if (gen !== this.gen) return;
        this.queue.shift();
        this.prefetch();
      }
    } finally {
      if (gen === this.gen) {
        this.pumping = false;
        this.waitIdle(gen);
      }
    }
  }

  waitIdle(gen) {
    const check = () => {
      if (gen !== this.gen || this.pumping || this.queue.length) return;
      if (this.isSounding()) this.later(80, check);
      else this.onIdle();
    };
    check();
  }

  // ms until the audio clock reaches `t`
  untilClock(t) {
    const a = audio();
    return a ? (t - a.ctx.currentTime) * 1000 : 0;
  }

  async waitClear(gen) {
    // wait for queued sound / silent speech to finish before a non-audio segment
    for (;;) {
      if (gen !== this.gen) return;
      const left = Math.max(
        this.untilClock(this.cursor),
        this.fake ? this.fake.until - performance.now() : 0
      );
      if (left <= 10) return;
      await new Promise((r) => this.later(Math.min(left, 200), r));
    }
  }

  async play(it, gen) {
    if (this.muted || !it.res) return this.playSilent(it, gen);
    let res = await it.res;
    if (gen !== this.gen) return undefined;
    if (!(res instanceof Response) || !res.ok || !res.body) {
      if (res instanceof Response && res.status === 204) return undefined;
      return this.playBrowser(it, gen);
    }
    const a = audio();
    if (!a) return this.playBrowser(it, gen);
    if (a.ctx.state === 'suspended') await a.ctx.resume().catch(() => {});
    const rate = Number(
      /rate=(\d+)/.exec(res.headers.get('X-Audio-Format') || '')?.[1] || 24000
    );
    const reader = res.body.getReader();
    // The sound arrives in pieces of 24 kHz PCM. Played as separate buffers,
    // the browser resamples every piece on its own and each seam clicks — a
    // crackle. So it is resampled here, continuously, to the output rate and
    // played in larger pieces that start exactly where the previous one ends.
    const outRate = a.ctx.sampleRate;
    const step = rate / outRate;
    let tail = null; // last input sample of the previous piece
    let pos = 0; // read position (input samples) from `tail`
    const resample = (f) => {
      let ext = f;
      if (tail !== null) {
        ext = new Float32Array(f.length + 1);
        ext[0] = tail;
        ext.set(f, 1);
      }
      const last = ext.length - 1;
      const out = new Float32Array(
        Math.max(0, Math.ceil((last - pos) / step) + 1)
      );
      let k = 0;
      let p = pos;
      while (p <= last) {
        const i = Math.floor(p);
        const x0 = ext[i];
        const x1 = i < last ? ext[i + 1] : x0;
        out[k++] = x0 + (x1 - x0) * (p - i);
        p += step;
      }
      pos = p - last;
      tail = ext[last];
      return out.subarray(0, k);
    };
    let pending = [];
    let pendingLen = 0;
    let carry = null;
    let first = true;
    const flush = () => {
      if (!pendingLen) return;
      const buf = a.ctx.createBuffer(1, pendingLen, outRate);
      const ch = buf.getChannelData(0);
      let o = 0;
      pending.forEach((p) => {
        ch.set(p, o);
        o += p.length;
      });
      pending = [];
      pendingLen = 0;
      const now = a.ctx.currentTime;
      const fakeLeft = this.fake
        ? (this.fake.until - performance.now()) / 1000
        : 0;
      const at = Math.max(this.cursor, now + 0.04, now + fakeLeft);
      if (at > this.cursor + 0.001) {
        // starting after a gap: fade in over 3 ms instead of a click
        const n = Math.min(ch.length, Math.round(outRate * 0.003));
        for (let i = 0; i < n; i++) ch[i] *= i / n;
      }
      if (first) {
        first = false;
        const seg = it;
        this.later(this.untilClock(at), () => this.onSegment(seg));
      }
      const src = a.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(a.out);
      src.start(at);
      this.cursor = at + buf.duration;
      this.sources.add(src);
      src.onended = () => this.sources.delete(src);
    };
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done || gen !== this.gen) break;
        let bytes = value;
        if (carry) {
          const m = new Uint8Array(carry.length + bytes.length);
          m.set(carry);
          m.set(bytes, carry.length);
          bytes = m;
          carry = null;
        }
        if (bytes.length % 2) {
          carry = bytes.slice(bytes.length - 1);
          bytes = bytes.subarray(0, bytes.length - 1);
        }
        if (!bytes.length) continue;
        const out = resample(pcmToFloat(bytes));
        pending.push(out);
        pendingLen += out.length;
        // the first piece starts the voice quickly, later ones are longer
        if (pendingLen >= outRate * (first ? 0.12 : 0.3)) flush();
      }
    } catch (e) {
      // aborted or network — what was received plays on
    }
    if (gen === this.gen) flush();
    if (first && gen === this.gen) return this.playBrowser(it, gen);
    return undefined;
  }

  async playSilent(it, gen) {
    await this.waitClear(gen);
    if (gen !== this.gen) return;
    const ms = Math.min(12000, 400 + it.text.length * 62);
    this.fake = { until: performance.now() + ms };
    this.onSegment(it);
    await new Promise((r) => this.later(ms, r));
  }

  async playBrowser(it, gen) {
    const synth = typeof window !== 'undefined' && window.speechSynthesis;
    if (!synth || typeof window.SpeechSynthesisUtterance !== 'function')
      return this.playSilent(it, gen);
    await this.waitClear(gen);
    if (gen !== this.gen) return undefined;
    return new Promise((resolve) => {
      const u = new window.SpeechSynthesisUtterance(it.text);
      const latin = (it.text.match(/[a-z]/gi) || []).length;
      const cyr = (it.text.match(/[а-яё]/gi) || []).length;
      u.lang = latin > cyr ? 'en-US' : 'ru-RU';
      const voice = synth
        .getVoices()
        .find(
          (v) => v.lang && v.lang.toLowerCase().startsWith(u.lang.slice(0, 2))
        );
      if (voice) u.voice = voice;
      u.rate = 1.03;
      const done = () => {
        this.fake = null;
        resolve();
      };
      u.onend = done;
      u.onerror = done;
      this.fake = { until: performance.now() + 60000 };
      this.onSegment(it);
      synth.speak(u);
      // never hang on a voice that does not report its end
      this.later(Math.min(60000, 2000 + it.text.length * 120), done);
    });
  }

  stop() {
    this.gen += 1;
    this.queue.forEach((it) => it.ctrl.abort());
    this.queue = [];
    this.sources.forEach((s) => {
      try {
        s.stop();
      } catch (e) {
        // already stopped
      }
    });
    this.sources.clear();
    this.timers.forEach((t) => clearTimeout(t));
    this.timers.clear();
    this.cursor = 0;
    this.fake = null;
    this.pumping = false;
    try {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    } catch (e) {
      // ignore
    }
  }
}
